import { FC, useCallback, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

import { Box, Spinner } from "@/components/ui";

import { STATES } from "./geo";
import {
  GeoFeature,
  geographyStyle,
  HatchDefs,
  MAP_HEIGHT,
  MAP_WIDTH,
  MapTooltip,
  STATES_URL,
  TooltipState,
  uniqueHatches,
} from "./MapParts";
import type { Paint } from "./types";

/* ── Types ─────────────────────────────────────── */

export interface NationalMapProps {
  /** Receives the state shapes once loaded (used to zoom on a state). */
  onFeatures?: (features: Record<string, GeoJSON.Feature>) => void;
  onSelect: (fips: string) => void;
  /** Paint per state FIPS; states without one are drawn as "no race". */
  paints: Record<string, Paint>;
  selected: string | null;
}

/* ── Constants ─────────────────────────────────── */

const STATE_FIPS = new Set(STATES.map((s) => s.fips));

/* ── Component ─────────────────────────────────── */

const NationalMap: FC<NationalMapProps> = ({
  onFeatures,
  onSelect,
  paints,
  selected,
}) => {
  const [loaded, setLoaded] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const hatches = useMemo(() => uniqueHatches(Object.values(paints)), [paints]);

  // Kept stable: react-simple-maps refetches the shapes whenever
  // parseGeographies changes, and the tooltip re-renders on every move.
  const parseStates = useCallback(
    (features: GeoJSON.Feature[]) => {
      const states = features.filter((f) => STATE_FIPS.has(String(f.id)));
      setLoaded(true);
      onFeatures?.(Object.fromEntries(states.map((f) => [String(f.id), f])));
      return states;
    },
    [onFeatures]
  );

  return (
    <Box
      style={{
        aspectRatio: MAP_WIDTH / MAP_HEIGHT,
        position: "relative",
        width: "100%",
      }}
    >
      {!loaded && (
        <Box className="absolute inset-0 items-center justify-center">
          <Spinner color="rgb(var(--color-primary-500))" size="small" />
        </Box>
      )}
      <ComposableMap
        height={MAP_HEIGHT}
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 1000 }}
        style={{ display: "block", height: "100%", width: "100%" }}
        width={MAP_WIDTH}
      >
        <HatchDefs hatches={hatches} />
        <Geographies geography={STATES_URL} parseGeographies={parseStates}>
          {({ geographies }: { geographies: GeoFeature[] }) =>
            geographies.map((geo) => {
              const paint = paints[geo.id];
              const show = (e: React.MouseEvent) =>
                setTooltip({
                  text: paint?.tooltip ?? "",
                  x: e.clientX,
                  y: e.clientY,
                });
              return (
                <Geography
                  geography={geo}
                  key={geo.rsmKey}
                  onClick={() => onSelect(geo.id)}
                  onMouseEnter={show}
                  onMouseLeave={() => setTooltip(null)}
                  onMouseMove={show}
                  style={geographyStyle(paint, selected === geo.id, true)}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
      <MapTooltip tooltip={tooltip} />
    </Box>
  );
};

export default NationalMap;
